import messengerApiService from '@widgets/messenger/services/messengerApiService'
import messengerWebSocketService from '@widgets/messenger/services/messengerWebSocketService'
import type { MessengerPreChatForm } from '@widgets/messenger/types'
import type { MessengerGetChatResponse } from '@widgets/messenger/types/api/rest'
import { isChatLoadingState, type MessengerChatState } from '@widgets/messenger/types/chat/chat'
import { useWidgetChatEventHandlers } from '@widgets/messenger/compositions/useWidgetChatEventHandlers'
import { useChatStore } from '@widgets/messenger/stores/useChatStore'
import { useChatLocalStorage } from '@widgets/messenger/compositions/useChatLocalStorage'
import { useWidgetStore } from '@widgets/messenger/stores/useWidgetStore'
import { debounce } from 'lodash-es'
import { useWidgetFormCache } from '@widgets/messenger/compositions/useWidgetFormCache'
import { useEditorAttachmentStore } from '@widgets/messenger/stores/useEditorAttachmentStore'
import { useWidgetChatPages } from '@widgets/messenger/compositions/useWidgetChatPages'
import { useWidgetChatEventHistory } from '@widgets/messenger/compositions/useWidgetChatEventHistory'
import { WebWidgetChannelType } from '@/data/models/domain/messages/settings/webWidget/types'

type ChatInitParams = {
  token: string
  email: string
  chatState: MessengerChatState
}

export const useWidgetChatInit = () => {
  const chatStore = useChatStore()
  const widgetStore = useWidgetStore()
  const editorAttachmentStore = useEditorAttachmentStore()
  const { handleChatEvent } = useWidgetChatEventHandlers()
  const chatLocalStorage = useChatLocalStorage()
  const formCache = useWidgetFormCache()
  const { fetchPage } = useWidgetChatPages()
  const { fetchHistory } = useWidgetChatEventHistory()

  const resetWebSocket = () => {
    if (messengerWebSocketService.hasConnection()) {
      messengerWebSocketService.removeAllListeners()
      messengerWebSocketService.disconnect()
    }
  }

  const initChat = async ({ token, email, chatState }: ChatInitParams) => {
    resetWebSocket()
    chatStore.resetStore()

    const { data: tokens } = await messengerApiService.getChatTokens(token)
    messengerWebSocketService.setCentrifugeTokens(tokens)
    messengerWebSocketService.addEventListener(tokens.subscriptionTokens[0].channel, handleChatEvent)
    messengerWebSocketService.addSubscriptionInitListener((ctx) => {
      const isRecoverFailed = ctx.wasRecovering && !ctx.recovered
      // If we tried to reinit using the refresh button without an internet connection
      const isReconnectedButStateIsEmpty = ctx.wasRecovering && isChatLoadingState(chatStore.chatState.value)
      if (isRecoverFailed || isReconnectedButStateIsEmpty) {
        reInitChat()
      }
    })
    const debouncedSetOffline = debounce(() => {
      chatStore.setIsConnected(false)
    }, 1000)
    messengerWebSocketService.addStatusChangeListener((isConnected) => {
      if (isConnected) {
        chatStore.setIsConnected(true)
        debouncedSetOffline.cancel()
      } else {
        debouncedSetOffline()
      }
    })
    messengerWebSocketService.connect()

    chatStore.setChatToken(token)
    chatStore.setUserEmail(email)
    chatStore.setChatState(chatState)

    await fetchPage(undefined, 'merge')
    await fetchHistory()
  }

  const initRejectedChat = (preChatForm: MessengerPreChatForm) => {
    resetWebSocket()
    chatStore.resetStore()
    chatStore.setChatState({
      type: 'ended',
      isNoAgents: true,
    })
    chatStore.addMessageListItem({
      type: 'initial',
      id: 'initial',
      messageTime: new Date().toISOString(),
      name: preChatForm.name,
      email: preChatForm.email,
      text: preChatForm.message,
    })
    chatStore.addMessageListItem({
      type: 'autoReply',
      id: 'autoReply',
      messageTime: new Date().toISOString(),
      text: widgetStore.widgetSettings.value.channels[WebWidgetChannelType.CHAT].rejectMessage,
    })
  }

  const startNewChat = async (preChatForm: MessengerPreChatForm) => {
    editorAttachmentStore.clearAttachments()
    formCache.resetForm('chat')

    let token: string
    try {
      const result = await messengerApiService.sendPreChat(widgetStore.uniqueCode.value, preChatForm)
      token = result.data.token
    } catch (e) {
      if (messengerApiService.isChatBlockedError(e)) {
        initRejectedChat(preChatForm)
        return
      }
      throw e
    }

    chatStore.setChatState({
      type: 'loading',
    })

    await initChat({
      token,
      email: preChatForm.email,
      chatState: {
        type: 'waiting',
      },
    })

    chatLocalStorage.set({ token, email: preChatForm.email })
  }

  // Returns true if chat was found, false otherwise
  const checkAndConnectToExistingChat = async (): Promise<boolean> => {
    const { token, email } = chatLocalStorage.get()
    if (!token || !email) {
      return false
    }

    let chatData: MessengerGetChatResponse
    try {
      const { data } = await messengerApiService.getChat(token)
      chatData = data
    } catch (e) {
      // BE response 400 when the chat is ended
      if (
        messengerApiService.isBadRequestError(e) &&
        e.response?.data?.errorCode === 'live_chat.conversation_is_not_available'
      ) {
        chatLocalStorage.remove()
        return false
      }
      throw e
    }

    const chatState: MessengerChatState = chatData.assignee
      ? {
          type: 'opened',
          agent: {
            name: [chatData.assignee.firstName, chatData.assignee.lastName].filter(Boolean).join(' '),
            avatarUrl: chatData.assignee.avatarHref,
          },
        }
      : {
          type: 'waiting',
        }
    await initChat({ token, email, chatState })
    return true
  }

  const reInitChat = async (): Promise<boolean | undefined> => {
    chatStore.resetStore()
    try {
      return await checkAndConnectToExistingChat()
    } catch (error) {
      chatStore.setChatState({
        type: 'loading',
        error,
      })
      return undefined
    }
  }

  return {
    startNewChat,
    checkAndConnectToExistingChat,
    reInitChat,
  }
}
